// ── Payment Terminals & Card Transactions ─────────────────────────────────────

/** Supported terminal device types */
export type TerminalDeviceType =
  | 'stripe_s700'
  | 'stripe_m2'
  | 'stripe_wisepos_e'
  | 'network_international'
  | 'sumup'
  | 'other';

/** Terminal connection status */
export type TerminalStatus = 'online' | 'offline' | 'busy' | 'needs_update';

/** Station assignment for terminal placement */
export type TerminalStation = 'counter' | 'bar' | 'waiter' | 'delivery';

/** Card entry method (how card was presented) */
export type CardEntryMethod = 'contactless' | 'chip' | 'swipe' | 'manual' | 'apple_pay' | 'google_pay';

/** Card transaction status */
export type CardTransactionStatus = 'pending' | 'processing' | 'succeeded' | 'failed' | 'cancelled' | 'refunded';

/** Settlement payout status */
export type SettlementStatus = 'pending' | 'in_transit' | 'paid' | 'failed' | 'cancelled';

/** Reconciliation status */
export type ReconciliationStatus = 'pending' | 'in_progress' | 'completed' | 'needs_review';

/** Reconciliation discrepancy types */
export type DiscrepancyType = 'missing_payment' | 'amount_mismatch' | 'duplicate_charge' | 'missing_order';

// ── Payment Terminal ──────────────────────────────────────────────────────────

export interface PaymentTerminal {
  id: string;
  restaurantId: string;
  organizationId: string;
  stripeReaderId: string | null;
  stripeLocationId: string | null;
  label: string;
  deviceType: TerminalDeviceType;
  serialNumber: string | null;
  status: TerminalStatus;
  lastSeenAt: string | null;
  ipAddress: string | null;
  firmwareVersion: string | null;
  batteryLevel: number | null;
  isActive: boolean;
  assignedStation: TerminalStation | null;
  createdAt: string;
  updatedAt: string;
}

// ── Card Transaction ──────────────────────────────────────────────────────────

export interface CardTransaction {
  id: string;
  restaurantId: string;
  organizationId: string;
  orderId: string | null;
  terminalId: string | null;
  stripePaymentIntentId: string | null;
  stripeChargeId: string | null;
  amount: number;
  tipAmount: number;
  currency: string;
  cardBrand: string | null;
  cardLast4: string | null;
  cardFunding: string | null;
  entryMethod: CardEntryMethod;
  status: CardTransactionStatus;
  failureReason: string | null;
  processorFee: number | null;
  netAmount: number | null;
  receiptUrl: string | null;
  receiptNumber: string | null;
  processedBy: string | null;
  processedByName: string | null;
  metadata: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

// ── Payment Settlement ────────────────────────────────────────────────────────

export interface PaymentSettlement {
  id: string;
  restaurantId: string;
  organizationId: string;
  stripePayoutId: string | null;
  stripeTransferId: string | null;
  settlementDate: string;
  grossAmount: number;
  processorFees: number;
  refundAmount: number;
  netAmount: number;
  currency: string;
  status: SettlementStatus;
  failureReason: string | null;
  bankName: string | null;
  bankLast4: string | null;
  bankReference: string | null;
  periodStart: string | null;
  periodEnd: string | null;
  transactionCount: number;
  estimatedArrival: string | null;
  depositedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

// ── Reconciliation ────────────────────────────────────────────────────────────

export interface ReconciliationDiscrepancy {
  type: DiscrepancyType;
  orderId?: string;
  stripeId?: string;
  expectedAmount?: number;
  actualAmount?: number;
  description: string;
}

export interface ReconciliationRecord {
  id: string;
  restaurantId: string;
  organizationId: string;
  periodStart: string;
  periodEnd: string;
  expectedAmount: number;
  actualAmount: number;
  variance: number;
  totalOrders: number;
  matchedOrders: number;
  unmatchedOrders: number;
  discrepancies: ReconciliationDiscrepancy[];
  status: ReconciliationStatus;
  reviewedBy: string | null;
  reviewedAt: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

// ── Terminal Stats (for dashboard) ────────────────────────────────────────────

export interface TerminalStats {
  totalTerminals: number;
  onlineTerminals: number;
  todayTransactions: number;
  todayVolume: number;
  avgTransactionValue: number;
}

export interface SettlementSummary {
  totalPaid: number;
  totalPending: number;
  totalInTransit: number;
  lastPayoutDate: string | null;
  lastPayoutAmount: number;
  nextEstimatedPayout: string | null;
}

// ── Stripe Terminal Device Labels ─────────────────────────────────────────────

export const TERMINAL_DEVICE_LABELS: Record<TerminalDeviceType, string> = {
  stripe_s700: 'Stripe Reader S700',
  stripe_m2: 'Stripe Reader M2',
  stripe_wisepos_e: 'Stripe WisePOS E',
  network_international: 'Network International',
  sumup: 'SumUp',
  other: 'Other',
};

export const TERMINAL_STATION_LABELS: Record<TerminalStation, string> = {
  counter: 'Counter',
  bar: 'Bar',
  waiter: 'Waiter Station',
  delivery: 'Delivery/Pickup',
};

export const CARD_BRAND_LABELS: Record<string, string> = {
  visa: 'Visa',
  mastercard: 'Mastercard',
  amex: 'American Express',
  mada: 'mada',
  discover: 'Discover',
  diners: 'Diners Club',
  jcb: 'JCB',
  unionpay: 'UnionPay',
};

export const ENTRY_METHOD_LABELS: Record<CardEntryMethod, string> = {
  contactless: 'Tap / Contactless',
  chip: 'Chip (EMV)',
  swipe: 'Magnetic Swipe',
  manual: 'Manual Entry',
  apple_pay: 'Apple Pay',
  google_pay: 'Google Pay',
};
